import React, { createContext, ReactElement, useContext, useReducer } from 'react';
import { none, isSome, some } from 'fp-ts/Option';
import { DashboardResult } from 'adapter/in/dashboards/context/Dashboard';

export enum LAYOUT_STATUS {
    READY = 'READY',
    FORCE_RESET = 'FORCE_RESET',
}

export enum ACTION_TYPE {
    SET_DASHBOARD = 'SET_DASHBOARD',
    SET_SELECTED_WIDGET = 'SET_SELECTED_WIDGET',
    SET_CLICKED_WIDGET = 'SET_CLICKED_WIDGET',
    SET_DASHBOARD_NAME = 'SET_DASHBOARD_NAME',
    MODAL_DISCARD_CHANGES = 'MODAL_DISCARD_CHANGES',
    SHOW_MODAL_EDIT_NAME = 'SHOW_MODAL_EDIT_NAME',
    SHOW_SIDE_PANEL_MENU = 'SHOW_SIDE_PANEL_MENU',
    RESET = 'RESET',
}

export const initialState: DashboardEditViewState = {
    dashboard: none,
    modalDiscardChanges: {
        show: false,
        viewCleanupAction: undefined,
    },
    showModalEditName: false,
    showSidePanelMenu: false,
};

type SetDashboard = {
    type: ACTION_TYPE.SET_DASHBOARD;
    payload: DashboardResult;
};

type SetDashboardName = {
    type: ACTION_TYPE.SET_DASHBOARD_NAME;
    payload: string;
};

type ModalDiscardChanges = {
    type: ACTION_TYPE.MODAL_DISCARD_CHANGES;
    payload: ModalDiscardChangesState;
};

type SetShowModalEditName = {
    type: ACTION_TYPE.SHOW_MODAL_EDIT_NAME;
    payload: boolean;
};

type SetShowSidePanelMenu = {
    type: ACTION_TYPE.SHOW_SIDE_PANEL_MENU;
    payload: boolean;
};

type Reset = {
    type: ACTION_TYPE.RESET;
    payload: null;
};

export type DashboardEditViewState = {
    dashboard: DashboardResult;
    modalDiscardChanges: ModalDiscardChangesState;
    showModalEditName: boolean;
    showSidePanelMenu: boolean;
};

export type ModalDiscardChangesState = {
    show: boolean;
    viewCleanupAction?: () => void;
    saveAndProceedAction?: () => void;
};

export type DashboardEditViewDispatch = {
    setDashboard: (dashboard: DashboardResult) => void;
    setDashboardNameAndCloseModal: (name: string) => void;
    setModalDiscardChanges: (show: boolean, viewCleanupAction?: () => void, saveAndProceedAction?: () => void) => void;
    setShowModalEditName: (showModalEditName: boolean) => void;
    setShowSidePanelMenu: (showSidePanelMenu: boolean) => void;

    reset: () => void;
};

type DashboardEditViewAction = SetDashboard | SetDashboardName | ModalDiscardChanges | SetShowModalEditName | SetShowSidePanelMenu | Reset;

export const useCaseEditDashboardReducer = (
    state: DashboardEditViewState,
    action: DashboardEditViewAction
    // eslint-disable-next-line consistent-return
): DashboardEditViewState => {
    const { type, payload } = action;
    // eslint-disable-next-line default-case
    switch (type) {
        case ACTION_TYPE.SET_DASHBOARD: {
            return {
                ...state,
                dashboard: payload,
            };
        }
        case ACTION_TYPE.SET_DASHBOARD_NAME: {
            return {
                ...state,
                dashboard: {
                    ...(isSome(state.dashboard) ? some(state.dashboard.value.copyWith({ name: payload })) : state.dashboard),
                },
                showModalEditName: false,
            };
        }

        case ACTION_TYPE.MODAL_DISCARD_CHANGES: {
            return {
                ...state,
                modalDiscardChanges: payload,
            };
        }

        case ACTION_TYPE.SHOW_MODAL_EDIT_NAME: {
            return {
                ...state,
                showModalEditName: payload,
            };
        }
        case ACTION_TYPE.RESET: {
            return { ...initialState };
        }
        case ACTION_TYPE.SHOW_SIDE_PANEL_MENU: {
            return {
                ...state,
                showSidePanelMenu: payload,
                modalDiscardChanges: {
                    ...state.modalDiscardChanges,
                    show: false,
                },
                showModalEditName: false,
            };
        }
    }
};

type DashboardEditProviderType = {
    children: ReactElement;
};

export const DashboardEditViewDispatchContext = createContext({} as DashboardEditViewDispatch);
export const DashboardEditViewStateContext = createContext(initialState);

const dispatchDashboardEdit = (): DashboardEditViewDispatch => useContext(DashboardEditViewDispatchContext);
const useDashboardEdit = (): DashboardEditViewState => useContext(DashboardEditViewStateContext);

const DashboardEditProvider = (props: DashboardEditProviderType): ReactElement => {
    const { children } = props;
    const [state, dispatch] = useReducer(useCaseEditDashboardReducer, initialState);

    const setDashboard = (dashboard: DashboardResult) =>
        dispatch({
            type: ACTION_TYPE.SET_DASHBOARD,
            payload: dashboard,
        });

    const setDashboardNameAndCloseModal = (name: string) =>
        dispatch({
            type: ACTION_TYPE.SET_DASHBOARD_NAME,
            payload: name,
        });

    const setShowModalEditName = (showModalEditName: boolean) =>
        dispatch({
            type: ACTION_TYPE.SHOW_MODAL_EDIT_NAME,
            payload: showModalEditName,
        });

    const setModalDiscardChanges = (show: boolean, viewCleanupAction?: () => void, saveAndProceedAction?: () => void) =>
        dispatch({
            type: ACTION_TYPE.MODAL_DISCARD_CHANGES,
            payload: {
                show,
                viewCleanupAction,
                saveAndProceedAction,
            },
        });

    const setShowSidePanelMenu = (showExitPanelMenu: boolean) =>
        dispatch({
            type: ACTION_TYPE.SHOW_SIDE_PANEL_MENU,
            payload: showExitPanelMenu,
        });

    const reset = () =>
        dispatch({
            type: ACTION_TYPE.RESET,
            payload: null,
        });

    return (
        <DashboardEditViewStateContext.Provider value={state}>
            <DashboardEditViewDispatchContext.Provider
                value={{
                    setDashboard,
                    setDashboardNameAndCloseModal,
                    setModalDiscardChanges,
                    setShowModalEditName,
                    setShowSidePanelMenu,
                    reset,
                }}
            >
                {children}
            </DashboardEditViewDispatchContext.Provider>
        </DashboardEditViewStateContext.Provider>
    );
};

export { DashboardEditProvider, dispatchDashboardEdit, useDashboardEdit };
